import React from 'react';
import { BatteryChargingIcon, PlugZapIcon } from 'lucide-react';
import { nl } from '../../../utils/home';
import type { EnergyState, UpsState } from '../../../types/beheer';
const HOURS = Array.from({
  length: 24
}, (_, h) => h);
export function EnergyPanel({
  energy,
  ups
}: {
  energy: EnergyState;
  ups: UpsState;
}) {
  const peak = Math.max(1, ...energy.hours.map(h => Math.max(h.use, h.solar)));
  const net = energy.useNow - energy.solarNow;
  return <div className="grid gap-8 rounded-3xl border border-white/[0.07] bg-night-900 p-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-10" data-id="element-217" data-mp="274pmjz1vx2c3">
      <div data-id="element-218" data-mp="1x8357k8rotet">
        <dl className="flex flex-wrap gap-x-10 gap-y-4" data-id="element-219" data-mp="1x9nxt4biu2a1">
          <div data-id="element-220" data-mp="1sa1k6trs6k0p">
            <dt className="text-[13px] text-ink-muted" data-id="element-221" data-mp="1sabeuunrtfcq">Verbruik nu</dt>
            <dd className="mt-1 font-display text-[36px] font-light leading-none tabular-nums text-ink" data-id="element-222" data-mp="3rmxat63a05x1">
              {nl(energy.useNow, 2)} <span className="text-[16px] text-ink-muted" data-id="element-223" data-mp="3mnkp4oo554w1">kW</span>
            </dd>
          </div>
          <div data-id="element-224" data-mp="jmdrdsqe1s7x">
            <dt className="text-[13px] text-ink-muted" data-id="element-225" data-mp="jllxqngcq4ka">Zon nu</dt>
            <dd className="mt-1 font-display text-[36px] font-light leading-none tabular-nums text-accent" data-id="element-226" data-mp="olgngnoe4o3r">
              {nl(energy.solarNow, 2)} <span className="text-[16px] text-ink-muted" data-id="element-227" data-mp="9pr7p79st77h">kW</span>
            </dd>
          </div>
          <div data-id="element-228" data-mp="eobwmajlo4gm">
            <dt className="text-[13px] text-ink-muted" data-id="element-229" data-mp="eo2ef94sr1lh">{net >= 0 ? 'Van het net' : 'Terug naar het net'}</dt>
            <dd className="mt-1 font-display text-[36px] font-light leading-none tabular-nums text-ink-soft" data-id="element-230" data-mp="12dwq3nddqk45">
              {nl(Math.abs(net), 2)} <span className="text-[16px] text-ink-muted" data-id="element-231" data-mp="snlbn6w2ksj0">kW</span>
            </dd>
          </div>
        </dl>

        <figure className="mt-8" data-id="element-232" data-mp="xlwkbeamowzn">
          <div className="flex h-28 items-end gap-1" role="img" aria-label={`Verbruik per uur vandaag, totaal ${nl(energy.todayUse)} kWh`} data-id="element-233" data-mp="xktw5e4ox8nf">
            {HOURS.map(h => {
            const d = energy.hours.find(x => x.hour === h);
            return <div key={h} className="relative flex h-full flex-1 items-end" data-id="element-234" data-mp="1zz7rh6az2sfi">
                  {d ? <>
                      <span className="w-full rounded-t-sm bg-white/25" style={{
                  height: `${d.use / peak * 100}%`
                }} data-id="element-235" data-mp="1v04mfluzsm7j" />
                      {d.solar > 0 && <span className="absolute bottom-0 left-1/4 w-1/2 rounded-t-sm bg-accent" style={{
                  height: `${d.solar / peak * 100}%`
                }} data-id="element-236" data-mp="1lzg96tdfapvp" />}
                    </> : <span className="h-px w-full bg-white/10" data-id="element-237" data-mp="1c82c0s8y225q" />}
                </div>;
          })}
          </div>
          <div className="mt-2 flex justify-between text-[11px] tabular-nums text-ink-muted" aria-hidden data-id="element-238" data-mp="177o377ziqo6n">
            <span data-id="element-239" data-mp="y9cg3fxv28iy">00</span>
            <span data-id="element-240" data-mp="y9vpu9dcxqlw">06</span>
            <span data-id="element-241" data-mp="y813cqmftqfp">12</span>
            <span data-id="element-242" data-mp="ybf27ovw7hie">18</span>
            <span data-id="element-243" data-mp="taj1tlar2dhq">24</span>
          </div>
          <figcaption className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-ink-muted" data-id="element-244" data-mp="tbkchonusxyx">
            <span className="flex items-center gap-1.5" data-id="element-245" data-mp="34wta7m51o0mn"><span className="h-2 w-2 rounded-sm bg-white/25" aria-hidden data-id="element-246" data-mp="39wpe7ldqrs27" /> Verbruik</span>
            <span className="flex items-center gap-1.5" data-id="element-247" data-mp="39yim1403cibs"><span className="h-2 w-2 rounded-sm bg-accent" aria-hidden data-id="element-248" data-mp="3eviu1kgi3v5j" /> Zon</span>
            <span className="tabular-nums" data-id="element-249" data-mp="3ixvxu3botizo">Vandaag {nl(energy.todayUse)} kWh · zon {nl(energy.todaySolar)} kWh · € {nl(energy.todayCost, 2)}</span>
          </figcaption>
        </figure>
      </div>

      <div className="border-t border-white/[0.06] pt-6 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0" data-id="element-250" data-mp="2r5y6eo9ek9px">
        <div className="flex items-center justify-between gap-3" data-id="element-251" data-mp="2v79qke9ic2qq">
          <h3 className="flex items-center gap-2 text-[15px] font-semibold text-ink" data-id="element-252" data-mp="2v5ymedc5gsz3">
            <BatteryChargingIcon className="h-4 w-4 text-ink-muted" aria-hidden data-id="element-253" data-mp="2zzt63grr97y6" /> UPS
          </h3>
          <span className={`flex items-center gap-1.5 whitespace-nowrap text-[13px] ${ups.onBattery ? 'text-amber' : 'text-ink-soft'}`} data-id="element-254" data-mp="23gdf9i33i65k">
            <PlugZapIcon className="h-3.5 w-3.5" aria-hidden data-id="element-255" data-mp="1odc5t84vd3ma" />
            {ups.onBattery ? 'Op batterij' : 'Netstroom'}
          </span>
        </div>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-256" data-mp="1jg28757btlnd">{ups.model}</p>
        <div className="mt-5 flex items-baseline gap-2" data-id="element-257" data-mp="1y4gniflxj2kt">
          <span className="font-display text-[36px] font-light leading-none tabular-nums text-ink" data-id="element-258" data-mp="1y5852mioyzs3">{ups.charge}%</span>
          <span className="text-[13px] text-ink-muted" data-id="element-259" data-mp="273tpyobmyqw1">geladen · {ups.runtimeMin} min looptijd</span>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/[0.08]" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={ups.loadPct} aria-label="Belasting UPS" data-id="element-260" data-mp="225s2lr76g5er">
          <div className="h-full rounded-full bg-ink-soft" style={{
          width: `${ups.loadPct}%`
        }} data-id="element-261" data-mp="2c2n32fn0gfvo" />
        </div>
        <p className="mt-1.5 text-[12px] tabular-nums text-ink-muted" data-id="element-262" data-mp="mngmmvlinff6">Belasting {ups.loadPct}% · laatste test {ups.lastTest}</p>
        <ul className="mt-5 space-y-1.5 text-[13px] text-ink-soft" data-id="element-263" data-mp="hqq7cahjcnut">
          {ups.protects.map(p => <li key={p} className="flex items-center gap-2" data-id="element-264" data-mp="2tdafurql9yj5">
              <span className="h-1 w-1 rounded-full bg-ink-muted" aria-hidden data-id="element-265" data-mp="2ybv7i2rrz9vz" /> {p}
            </li>)}
        </ul>
      </div>
    </div>;
}